import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authQueryKeys } from '@/hooks/queries/auth.queries';
import {
  useCancelBookingDetail,
  useConfirmBookingDetail,
  useProfileBookingDetail,
} from '@/hooks/queries/useProfileBookings';
import { useAuth } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/lib/api/error';
import {
  canCancelProfileBooking,
  canConfirmProfileBooking,
  canRescheduleProfileBooking,
  getProfileBookingStatusLabel,
} from '@/lib/profile-bookings';
import { AuthService } from '@/services/auth.service';
import type { ProfileBooking } from '@/types/profile';
import { useQueryClient } from '@tanstack/react-query';
import { format, isToday, isTomorrow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ArrowLeft, Building2, Calendar, Clock, Loader2, RefreshCw, Scissors, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  currency: 'BRL',
  style: 'currency',
});

function formatBookingDate(booking: Pick<ProfileBooking, 'date'>) {
  const bookingDateTime = new Date(`${booking.date}T00:00:00`);

  if (Number.isNaN(bookingDateTime.getTime())) {
    return booking.date || 'Data a confirmar';
  }

  if (isToday(bookingDateTime)) {
    return 'Hoje';
  }

  if (isTomorrow(bookingDateTime)) {
    return 'Amanhã';
  }

  return format(bookingDateTime, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

function formatServices(booking: Pick<ProfileBooking, 'services'>) {
  if (booking.services.length === 0) {
    return 'Serviço a confirmar';
  }

  return booking.services.map((service) => service.name).join(', ');
}

function getBookingTotalLabel(booking: Pick<ProfileBooking, 'totalPrice'>) {
  return typeof booking.totalPrice === 'number' ? currencyFormatter.format(booking.totalPrice) : 'Valor a confirmar';
}

const BookingManagement = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const token = searchParams.get('token')?.trim() || null;
  const [tokenStatus, setTokenStatus] = useState<'idle' | 'authenticating' | 'authenticated' | 'error'>(
    token ? 'idle' : 'authenticated',
  );
  const [actionInFlight, setActionInFlight] = useState<'confirm' | 'cancel' | null>(null);
  const canLoadBooking = Boolean(bookingId) && (isAuthenticated || (Boolean(token) && tokenStatus === 'authenticated'));
  const bookingQuery = useProfileBookingDetail(bookingId, canLoadBooking);
  const { confirmBooking } = useConfirmBookingDetail(bookingId, user?.id);
  const { cancelBooking } = useCancelBookingDetail(bookingId, user?.id);
  const booking = bookingQuery.booking;

  useEffect(() => {
    if (!bookingId || !token) {
      return;
    }

    let isMounted = true;

    async function authenticateWithToken() {
      setTokenStatus('authenticating');

      try {
        const response = await AuthService.signInWithBookingManagementToken({ token });

        queryClient.setQueryData(authQueryKeys.profile(), response.user);
        void queryClient.invalidateQueries({ queryKey: authQueryKeys.profile() });

        if (!isMounted) {
          return;
        }

        setTokenStatus('authenticated');
        navigate(`/agendamentos/${bookingId}`, { replace: true });
      } catch {
        if (isMounted) {
          setTokenStatus('error');
        }
      }
    }

    void authenticateWithToken();

    return () => {
      isMounted = false;
    };
  }, [bookingId, navigate, queryClient, token]);

  useEffect(() => {
    if (token || isAuthLoading || isAuthenticated || tokenStatus === 'authenticating') {
      return;
    }

    navigate('/entrar', {
      replace: true,
      state: { from: bookingId ? `/agendamentos/${bookingId}` : '/agendamentos' },
    });
  }, [bookingId, isAuthenticated, isAuthLoading, navigate, token, tokenStatus]);

  const canConfirm = useMemo(() => (booking ? canConfirmProfileBooking(booking) : false), [booking]);
  const canCancel = useMemo(() => (booking ? canCancelProfileBooking(booking) : false), [booking]);
  const canReschedule = useMemo(() => (booking ? canRescheduleProfileBooking(booking) : false), [booking]);

  const handleConfirm = async () => {
    if (!bookingId) {
      return;
    }

    try {
      setActionInFlight('confirm');
      await confirmBooking(bookingId);
      toast.success('Agendamento confirmado', {
        description: 'Seu horário foi confirmado com sucesso.',
      });
    } catch (error) {
      toast.error('Não foi possível confirmar', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setActionInFlight(null);
    }
  };

  const handleCancel = async () => {
    if (!bookingId) {
      return;
    }

    try {
      setActionInFlight('cancel');
      await cancelBooking(bookingId);
      toast.success('Agendamento cancelado', {
        description: 'Seu horário foi atualizado com sucesso.',
      });
    } catch (error) {
      toast.error('Não foi possível cancelar', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setActionInFlight(null);
    }
  };

  const handleReschedule = () => {
    if (!booking?.companySlug) {
      toast.error('Não foi possível reagendar', {
        description: 'Este agendamento não tem estabelecimento vinculado.',
      });
      return;
    }

    navigate(`/book/${booking.companySlug}`, {
      state: {
        rescheduleFromBookingId: booking.id,
        selectedServices: booking.services.map((service) => service.id),
      },
    });
  };

  if (!bookingId) {
    return (
      <BookingManagementShell title="Agendamento">
        <StateCard title="Agendamento não encontrado" description="O link acessado não possui um agendamento válido." />
      </BookingManagementShell>
    );
  }

  if (tokenStatus === 'authenticating' || isAuthLoading || (tokenStatus === 'idle' && token)) {
    return (
      <BookingManagementShell title="Agendamento">
        <LoadingCard label="Validando acesso ao agendamento..." />
      </BookingManagementShell>
    );
  }

  if (tokenStatus === 'error') {
    return (
      <BookingManagementShell title="Agendamento">
        <StateCard
          title="Link expirado ou inválido"
          description="Entre na sua conta para tentar acessar este agendamento."
          action={
            <Button
              type="button"
              onClick={() => navigate('/entrar', { state: { from: `/agendamentos/${bookingId}` } })}
            >
              Entrar
            </Button>
          }
        />
      </BookingManagementShell>
    );
  }

  if (!isAuthenticated && !token && tokenStatus !== 'authenticated') {
    return (
      <BookingManagementShell title="Agendamento">
        <LoadingCard label="Redirecionando para o login..." />
      </BookingManagementShell>
    );
  }

  if (bookingQuery.isLoading) {
    return (
      <BookingManagementShell title="Agendamento">
        <LoadingCard label="Carregando agendamento..." />
      </BookingManagementShell>
    );
  }

  if (bookingQuery.isError) {
    return (
      <BookingManagementShell title="Agendamento">
        <StateCard
          title="Não foi possível carregar"
          description="Tente novamente em instantes."
          action={
            <Button type="button" onClick={() => void bookingQuery.refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Tentar novamente
            </Button>
          }
        />
      </BookingManagementShell>
    );
  }

  if (!booking) {
    return (
      <BookingManagementShell title="Agendamento">
        <StateCard title="Agendamento não encontrado" description="Não encontramos os dados deste horário." />
      </BookingManagementShell>
    );
  }

  return (
    <BookingManagementShell title="Gerenciar agendamento">
      <div className="container mx-auto max-w-4xl space-y-5 py-6">
        <section className="border-border bg-card rounded-2xl border p-5 md:p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <span className="bg-primary/10 text-primary inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold">
                {getProfileBookingStatusLabel(booking)}
              </span>
              <h2 className="text-foreground mt-3 text-2xl font-bold">{booking.companyName}</h2>
              <p className="text-muted-foreground mt-1 text-sm">{formatServices(booking)}</p>
            </div>

            {booking.companySlug && (
              <Button asChild variant="outline" className="md:min-w-44">
                <Link to={`/${booking.companySlug}`}>Ver estabelecimento</Link>
              </Button>
            )}
          </div>
        </section>

        <div className="grid gap-4 md:grid-cols-2">
          <InfoCard icon={<Calendar className="h-4 w-4" />} label="Data" value={formatBookingDate(booking)} />
          <InfoCard icon={<Clock className="h-4 w-4" />} label="Horário" value={booking.time || 'A confirmar'} />
          <InfoCard icon={<User className="h-4 w-4" />} label="Profissional" value={booking.professionalName} />
          <InfoCard
            icon={<Scissors className="h-4 w-4" />}
            label="Serviços"
            value={formatServices(booking)}
            description={
              booking.totalDuration
                ? `${booking.totalDuration} minutos · ${getBookingTotalLabel(booking)}`
                : getBookingTotalLabel(booking)
            }
          />
        </div>

        <Card className="border-border">
          <CardHeader>
            <CardTitle>Ações</CardTitle>
            <CardDescription>As opções aparecem conforme o status atual do agendamento.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {canConfirm && (
              <Button type="button" className="sm:min-w-40" disabled={Boolean(actionInFlight)} onClick={handleConfirm}>
                {actionInFlight === 'confirm' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </Button>
            )}

            {canReschedule && (
              <Button
                type="button"
                variant={canConfirm ? 'outline' : 'default'}
                className="sm:min-w-40"
                disabled={Boolean(actionInFlight)}
                onClick={handleReschedule}
              >
                Reagendar
              </Button>
            )}

            {canCancel && (
              <Button
                type="button"
                variant="outline"
                className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive sm:min-w-40"
                disabled={Boolean(actionInFlight)}
                onClick={handleCancel}
              >
                {actionInFlight === 'cancel' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  'Cancelar'
                )}
              </Button>
            )}

            {!canConfirm && !canCancel && !canReschedule && (
              <p className="text-muted-foreground text-sm">Nenhuma ação disponível para este status.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </BookingManagementShell>
  );
};

function BookingManagementShell({ children, title }: { children: ReactNode; title: string }) {
  const navigate = useNavigate();

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-background/95 sticky top-0 z-40 border-b backdrop-blur-sm">
        <div className="container flex h-14 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:bg-muted -ml-2 flex h-10 w-10 items-center justify-center rounded-lg transition-colors"
          >
            <ArrowLeft className="text-foreground h-5 w-5" />
          </button>
          <h1 className="text-foreground flex-1 text-center text-base font-semibold">{title}</h1>
          <div className="h-10 w-10 shrink-0" aria-hidden="true" />
        </div>
      </header>
      {children}
    </div>
  );
}

function LoadingCard({ label }: { label: string }) {
  return (
    <div className="container mx-auto max-w-4xl py-6">
      <Card className="border-border">
        <CardContent className="text-muted-foreground flex items-center gap-3 p-6 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {label}
        </CardContent>
      </Card>
    </div>
  );
}

function StateCard({ action, description, title }: { action?: ReactNode; description: string; title: string }) {
  return (
    <div className="container mx-auto max-w-4xl py-6">
      <Card className="border-border">
        <CardContent className="py-12 text-center">
          <Building2 className="text-muted-foreground/30 mx-auto mb-3 h-10 w-10" />
          <p className="text-foreground font-semibold">{title}</p>
          <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">{description}</p>
          {action && <div className="mt-5">{action}</div>}
        </CardContent>
      </Card>
    </div>
  );
}

function InfoCard({
  description,
  icon,
  label,
  value,
}: {
  description?: string;
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card className="border-border">
      <CardContent className="flex items-start gap-3 p-5">
        <span className="bg-primary/10 text-primary mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg">
          {icon}
        </span>
        <div>
          <p className="text-muted-foreground text-sm">{label}</p>
          <p className="text-foreground mt-1 font-semibold">{value}</p>
          {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default BookingManagement;
